import * as Sentry from '@sentry/react';
import 'firebase/auth';
import {
  initFirebase,
  refreshSession,
  signOut,
  signOutAndRedirect,
} from 'lib/AuthClient/authentication';
import { getAuthHeadersFromCookie } from 'lib/AuthClient/cookie';
import SignIn from 'pages/authentication/sign-in/SignIn';
import React, { useEffect, useRef, useState } from 'react';
import { Route, Router, Switch } from 'react-router-dom';
import Swal from 'sweetalert2';
import { browserHistory } from './browserHistory';
import ScrollToTop from './ScrollToTop';
import { Loader } from './components/Loader/Loader';
import { CourierNavBar } from './components/navbar/CourierNavBar';
import { initializeHubConfig } from './const/HubConfig';
import { AuthHeaders } from './lib/AuthClient/cookieTypes';
import {
  getUserSelectedRegion,
  selectUserRegion,
} from './lib/AuthClient/userPreferences';
import { settings } from './settings';
import { Region } from './types/orderTypes';
import analyticsWrapper from './utils/analyticsWrapper';
import { RoleType } from './types/userTypes';
import BankAccountOnboarding from './pages/dipps/BankAccountOnboarding';
import CourierOnboarding from './pages/dipps/CourierOnboarding';
import { DispatchRouter } from './DispatchRouter';
import { AdminNavBar } from './components/navbar/Navbar';
import FloatingMenu from './components/FloatingMenu/FloatingMenu';
import GoToWidget from './pages/Dashboard/widgets/orders/GoToWidget';
import firebase from 'firebase';
import PageVisibility from 'react-page-visibility';
import dayjs from 'dayjs';
import { checkBackendAccess } from './utils/checkBackendAccess';

const UserAuth = 'user-authentication';

function getSelectedRegion(authHeaders: AuthHeaders): Region {
  const selectedRegion = getUserSelectedRegion();
  if (
    window.location.pathname === '/monitor' &&
    sessionStorage.getItem('monitorViewRegion')
  ) {
    return sessionStorage.getItem('monitorViewRegion') as Region;
  }
  if (!selectedRegion) {
    const defaultRegion = authHeaders?.availableRegions?.includes(Region.OSLO)
      ? Region.OSLO
      : authHeaders?.availableRegions[0];
    selectUserRegion(defaultRegion);
    return defaultRegion;
  }
  return selectedRegion;
}

export const AppRouter = () => {
  const [roleType, setRoleType] = useState<RoleType>(undefined);
  const [selectedRegion, setSelectedRegion] = useState<Region>(undefined);
  const [lastTokenCheck, setLastTokenCheck] = useState<dayjs.Dayjs>(dayjs());

  const timerId = useRef<NodeJS.Timeout>(undefined);
  const authHeaders = useRef<AuthHeaders | null>(getAuthHeadersFromCookie());

  browserHistory.listen(location => {
    analyticsWrapper.pageview(location.pathname);
  });

  useEffect(() => {
    const verifyAuth = async () => {
      const firebaseInitiated = firebase.apps.length > 0;
      if (!firebaseInitiated) {
        initFirebase();
      }

      authHeaders.current = getAuthHeadersFromCookie();
      if (!authHeaders.current) {
        analyticsWrapper.event({
          category: UserAuth,
          action: 'no-session-sign-in',
        });
        await signOut();
      } else if (
        // Dev corner case when switching env
        authHeaders.current?.authorizationBackend !== settings.newApiUrl ||
        // Transition to make everyone have available regions
        !authHeaders.current?.availableRegions
      ) {
        analyticsWrapper.event({
          category: UserAuth,
          action: 'session-expired-bad-headers',
        });
        await signOutAndRedirect();
      } else {
        try {
          console.log('Refreshing session');
          await refreshSession();
          analyticsWrapper.event({
            category: UserAuth,
            action: 'session-refreshed',
          });
          /* initializeSessionData */
          /* All the data that should be just fetched once per session should be initialized here */
          await initializeHubConfig();
        } catch (e) {
          console.log('Could not refresh, signing out');
          analyticsWrapper.event({
            category: UserAuth,
            action: 'session-expired-timer',
          });
          await signOutAndRedirect();
        }

        /* addUserInfoToSentry */
        Sentry.configureScope(scope => {
          scope.setUser({
            id: authHeaders.current.uid,
            username: authHeaders.current.username,
            email: authHeaders.current.email,
          });
        });

        const region = getSelectedRegion(authHeaders.current);
        setRoleType(authHeaders.current.role);
        setSelectedRegion(region);
      }
    };
    verifyAuth().catch(err => {
      console.error(err);
    });

    if (!timerId.current) {
      timerId.current = setInterval(async () => {
        if (authHeaders.current) {
          try {
            await refreshSession();
          } catch (e) {
            analyticsWrapper.event({
              category: UserAuth,
              action: 'session-expired-timer',
            });
            await Swal.fire({
              title: 'Session ended',
              text: `We're sorry. It looks like you have been idle too long. You will now be sent to the login screen.`,
              icon: 'warning',
              showCancelButton: false,
              confirmButtonText: 'Go to login',
            });
            await signOutAndRedirect();
          }
        }
      }, 10 * 60 * 1000);
    }

    return () => {
      timerId.current && clearInterval(timerId.current);
    };
  }, []);

  const onVisibilityChange = async (isVisible: boolean) => {
    if (isVisible && dayjs().diff(lastTokenCheck, 'm') > 5) {
      // Re-check login when tab is in foreground and last check is more than 5 min ago
      setLastTokenCheck(dayjs());
      const loginStillValid = await checkBackendAccess();
      if (!loginStillValid) {
        await signOutAndRedirect();
      }
    }
  };

  const onRegionSelected = (region: Region): void => {
    selectUserRegion(region);
    if (window.location.pathname === '/monitor') {
      sessionStorage.setItem('monitorViewRegion', region);
    }

    setSelectedRegion(region);
  };

  const selectedRegionParam =
    selectedRegion || (authHeaders && getSelectedRegion(authHeaders.current));

  const isLoggedIn = authHeaders.current && selectedRegionParam;

  if (isLoggedIn) {
    return (
      <PageVisibility onChange={onVisibilityChange}>
        <Router history={browserHistory}>
          <ScrollToTop history={browserHistory} />
          {roleType === RoleType.ADMIN && (
            <>
              <AdminNavBar
                availableRegions={authHeaders.current.availableRegions}
                selectedRegion={selectedRegionParam}
                userText={authHeaders.current.username}
                changeRegionCallback={region => onRegionSelected(region)}
                limitedAccess={authHeaders.current.limitedAccess}
              />
              <FloatingMenu>
                <GoToWidget floatingWidget />
              </FloatingMenu>
            </>
          )}
          {roleType === RoleType.COURIER && (
            <CourierNavBar
              selectedRegion={selectedRegionParam}
              availableRegions={authHeaders.current.availableRegions}
              changeRegionCallback={region => onRegionSelected(region)}
            />
          )}
          {roleType !== RoleType.COURIER && roleType !== RoleType.ADMIN && (
            <div
              style={{
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                height: '100%',
                display: 'flex',
              }}
            >
              <Loader size="xxl" type="spinner" />
            </div>
          )}
          <DispatchRouter
            region={selectedRegionParam}
            limitedAccess={authHeaders.current.limitedAccess}
            roleType={authHeaders.current.role}
          />
        </Router>
      </PageVisibility>
    );
  } else {
    return (
      <Router history={browserHistory}>
        <Switch>
          <Route path={'/reset'} exact>
            <div>
              <SignIn showSignInBox={false} />
            </div>
          </Route>
          <Route path={'/onboarding'}>
            <CourierOnboarding />
          </Route>
          <Route path={'/onboarding-stage-2'} exact>
            <BankAccountOnboarding />
          </Route>
          <Route>
            <SignIn showSignInBox={true} />
          </Route>
        </Switch>
      </Router>
    );
  }
};
